import { useState, useMemo, useEffect } from "react";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import { Card, CardContent } from "../../ui/card";
import { Globe, ShieldCheck, ChevronLeft, ChevronRight, ExternalLink } from "lucide-react";
import RequestConnectAccessDialog from "../TenantUserEndpoints/RequestConnectAccessDialog";
import { WebUrlRecord } from "../WebUrls/interface";
import WebURLConnectAccess from "./WebURLConnectAccess";
import { myLocalStorage } from "../../StorageHelper";
import useNotificationsStore from "../../../services/notificationStore";
import moment from "moment";
import { NGROK } from "../../../APIs";
import axios from "axios";
import useUserStore from "../../../services/userStore";
import { v4 as uuidv4 } from "uuid";
import { useToast } from "../../../hooks/use-toast";


interface TenantWebUrlsTableProps {
    webUrls: WebUrlRecord[];
    onConnect?: (urlId: string) => void;
    refetchWebUrls?: () => void;
    searchQuery: string;
}

const URLS_PER_PAGE = 8;

export default function TenantWebUrlsTable({ webUrls, onConnect, refetchWebUrls, searchQuery = "" }: TenantWebUrlsTableProps) {
    const [showAccessDialog, setShowAccessDialog] = useState(false);
    const [selectedUrl, setSelectedUrl] = useState<WebUrlRecord | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const tenantName = myLocalStorage.getItem("latestTenant")?.tenantName || "";
    const { userWebUrlApprovedNotifications } = useNotificationsStore();
    const userData = useUserStore((state) => state.user);
    const access_token = myLocalStorage.getItem("access_token");
    const { toast } = useToast();


    let userAccess = userData.role;
    let loginemail = userData.email;

    const fetchResourceLevel = userWebUrlApprovedNotifications?.filter(
        (userWebUrlNotification: any) =>
            userWebUrlNotification.requestStatus === "APPROVED",
    );


    const checkAccessTiming = (weburl: any) => {
        const record = fetchResourceLevel.find(
            (data: any) => data.accessUrl === weburl?.url,
        );

        if (!record) return false;

        const schedules = record.schedules;

        if (!schedules || schedules.length === 0) {
            return record;
        }

        const nowUTC = moment.utc();

        const isActive = schedules.some((schedule: any) => {
            const scheduledStartTime = moment.utc(schedule.scheduledStartTime);
            const scheduledEndTime = moment.utc(schedule.scheduledEndTime);
            const isWithinSchedule = nowUTC.isBetween(
                scheduledStartTime,
                scheduledEndTime,
                null,
                "[]",
            );
            return isWithinSchedule;
        });

        return isActive;
    };

    const getFaviconUrl = (url: string) => {
        try {
            const domain = new URL(url).hostname;
            return `https://www.google.com/s2/favicons?sz=64&domain=${domain}`;
        } catch {
            return undefined;
        }
    };


    const userloginToUrl = async (item: any) => {
        const filteredResourceUser = fetchResourceLevel.find(
            (user: any) => user.accessUrl === item?.url,
        );
        if (filteredResourceUser) {
            const isPremanentAccess = filteredResourceUser.privilegeLevel;
            const accessCredential = filteredResourceUser.accessCredential;
            const matchedUser = item?.credentialsList.find(
                (user: any) => user.username === accessCredential,
            );
            const result = matchedUser
                ? {
                    email: matchedUser.username,
                    authValutTags: matchedUser.id,
                }
                : null;
            let differenceInMilliseconds = 0;
            if (isPremanentAccess === "DYNAMIC") {
                const isUTC = filteredResourceUser.accessTimeOverAt.endsWith("Z");
                let dateTimeStr = "";
                if (!isUTC) {
                    dateTimeStr = filteredResourceUser.accessTimeOverAt + "Z";
                }
                const dateTime = new Date(dateTimeStr);
                const now = new Date();
                let istOffset = 5.5 * 60;
                let pendingTime_ist: any = new Date(
                    dateTime.getTime() + istOffset * 60 * 1000,
                );
                if (
                    filteredResourceUser.schedules &&
                    filteredResourceUser.schedules.length > 0
                ) {
                    const schedule = filteredResourceUser.schedules[0];
                    const utcDate = new Date(schedule.scheduledEndTime);
                    const istOffsetMs = (5 * 60 + 30) * 60 * 1000;
                    const istDate = new Date(utcDate.getTime() + istOffsetMs);
                    pendingTime_ist = new Date(istDate.getTime() + istOffset * 60 * 1000);
                }

                let currentDate_ist: any = new Date(now.getTime() + istOffset * 60 * 1000);
                differenceInMilliseconds = currentDate_ist - pendingTime_ist;
            }

            const { authValutTags, email } = result ?? { authValutTags: [], email: "" };
            let randomUUID = uuidv4();
            let payload = {
                tokenpasskey: randomUUID,
                userTag: authValutTags,
                email: email,
                token: access_token,
                url: NGROK,
                executeURL: item.url,
                role: isPremanentAccess === "PERMANENT" ? "" : userAccess,
                duration:
                    isPremanentAccess === "PERMANENT"
                        ? 0
                        : Math.abs(differenceInMilliseconds),
                tenantName: tenantName,
                webId: item.id,
                loginemail: loginemail,
                isRecordingOff: !item?.recording,
            };
            const response = await axios
                .get(`${NGROK}/api/websites/save-web-url-click?uuid=${randomUUID}`)
                .then(async () => {
                    await axios
                        .post("http://localhost:5051/start-automation", payload)
                        .then((data) => { })
                        .catch((e) => {
                            const message = e?.response?.data?.message;
                            toast({
                                title: "Error",
                                description: `${message} || WSWebApp not running... Make sure that the zip is downloaded and WSWebApp is running`,
                                variant: "destructive",
                            });
                        });
                })
                .catch((e) => {
                    const message = e?.response?.data?.message;
                    toast({
                        title: "Error",
                        description: `${message} || WSWebApp not running... Make sure that the zip is downloaded and WSWebApp is running`,
                        variant: "destructive",
                    });
                });
        }
    };


    const handleConnect = (urlId: string) => {
        const url = webUrls.find(u => u.id === Number(urlId));
        if (url) {
            setSelectedUrl(url);
            setShowAccessDialog(true);
        }
        onConnect?.(urlId);
    };

    // Map the webUrl response to match the expected WebUrl interface
    const webUrlsMapped = useMemo(() => {
        const mapped = webUrls.map((webUrl) => ({
            id: String(webUrl.id),
            url: webUrl.url,
            domain: webUrl.tenantName,
            description: webUrl.isManaged ? "Managed by admin" : "Unmanaged URL",
            icon: "Globe",
            createdAt: webUrl.date,
            createdBy: webUrl.createdBy,
            credentialsList: webUrl.auth.map((auth) => ({
                id: auth.authValutTags,
                username: auth.email,
            })),
            credentials: webUrl.auth.length,
            management: {
                published: webUrl.publishedroleIds.length > 0,
                roles: webUrl.publishedroleIds.map(id => String(id)),
            },
            recording: webUrl.recordingenabled,
        }));

        if (searchQuery.trim()) {
            return mapped.filter((webUrl) =>
                webUrl.url.toLowerCase().includes(searchQuery.toLowerCase()) ||
                webUrl.description.toLowerCase().includes(searchQuery.toLowerCase())
            );
        }

        return mapped;
    }, [webUrls, searchQuery]);

    const paginatedUrls = useMemo(() => {
        const startIndex = (currentPage - 1) * URLS_PER_PAGE;
        const endIndex = startIndex + URLS_PER_PAGE;
        return webUrlsMapped.slice(startIndex, endIndex);
    }, [webUrlsMapped, currentPage]);

    const totalPages = Math.ceil(webUrlsMapped.length / URLS_PER_PAGE);

    useMemo(() => {
        setCurrentPage(1);
    }, [searchQuery]);

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };
    useEffect(() => {
        if (!refetchWebUrls) return;

        const interval = setInterval(() => {
            refetchWebUrls();
        }, 10000);

        return () => clearInterval(interval);
    }, [refetchWebUrls]);
    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between p-4 bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 rounded-lg border">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
                        <Globe className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                        <h3 className="font-semibold text-slate-900 dark:text-slate-100">
                            Available Web URIs
                        </h3>
                        <p className="text-sm text-slate-600 dark:text-slate-400">
                            {webUrlsMapped.length} web URI{webUrlsMapped.length !== 1 ? 's' : ''} assigned to your roles
                        </p>
                    </div>
                </div>

            </div>

            {/* Web URL Cards Grid */}
            <div className="grid gap-4 md:grid-cols-2">
                {[...(paginatedUrls ?? [])].sort((a, b) => b.id.localeCompare(a.id)).map((webUrl) => (
                    <Card
                        key={webUrl.id}
                        className="border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900"
                    >
                        <CardContent className="p-6">
                            {/* Web URL Header */}
                            <div className="flex items-start justify-between mb-4">
                                <div className="flex items-center space-x-4">
                                    <div className="w-10 h-10 bg-gradient-to-br from-blue-100 to-blue-50 dark:from-blue-900/20 dark:to-blue-800/10 rounded-lg flex items-center justify-center">
                                        {getFaviconUrl(webUrl.url) ? (
                                            <img
                                                src={getFaviconUrl(webUrl.url)}
                                                alt="favicon"
                                                className="w-6 h-6 object-contain"
                                            />
                                        ) : (
                                            <Globe className="w-6 h-6 text-blue-600" />
                                        )}
                                    </div>
                                    <div className="flex-1">
                                        <h4 className="font-semibold text-lg text-slate-900 dark:text-slate-100" data-testid={`text-weburl-name-${webUrl.id}`}>
                                            {webUrl.url}
                                        </h4>
                                    </div>
                                </div>
                            </div>

                            {/* Web URL Description */}
                            {webUrl.description && (
                                <div className="mb-4">
                                    <p className="text-sm text-slate-600 dark:text-slate-400">
                                        {webUrl.description}
                                    </p>
                                </div>
                            )}

                            {/* Status Pills */}
                            <div className="flex flex-wrap gap-2 mb-4">
                                <Badge variant="secondary" className="text-xs">
                                    {webUrl.credentials} credential{webUrl.credentials !== 1 ? 's' : ''}
                                </Badge>
                                {webUrl.recording && (
                                    <Badge variant="default" className="text-xs">
                                        Recording
                                    </Badge>
                                )}
                            </div>

                            {/* Action Button */}
                            <div className="pt-3 border-t border-slate-100 dark:border-slate-700">

                                {checkAccessTiming(webUrl) ? <Button
                                    size="sm"
                                    onClick={() => userloginToUrl(webUrl)}
                                    data-testid={`button-connect-${webUrl.id}`}
                                    variant="outline"
                                    className="w-full gap-2 border-blue-200 dark:border-blue-700 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:border-blue-300 dark:hover:border-blue-600 bg-blue-50 dark:bg-blue-950/30 hover:bg-blue-100 dark:hover:bg-blue-950/50"
                                >
                                    <ShieldCheck className="w-4 h-4" />
                                    Connect
                                </Button> : <Button
                                    size="sm"
                                    onClick={() => handleConnect(webUrl.id)}
                                    data-testid={`button-connect-${webUrl.id}`}
                                    variant="outline"
                                    className="w-full gap-2 border-blue-200 dark:border-blue-700 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:border-blue-300 dark:hover:border-blue-600 bg-blue-50 dark:bg-blue-950/30 hover:bg-blue-100 dark:hover:bg-blue-950/50"
                                >
                                    <ShieldCheck className="w-4 h-4" />
                                    Request Connect Access
                                </Button>}

                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>

            {webUrlsMapped.length === 0 && (
                <Card className="border-2 border-dashed border-slate-300 dark:border-slate-600">
                    <CardContent className="p-12 text-center">
                        <Globe className="w-12 h-12 mx-auto mb-4 text-slate-400" />
                        <h3 className="text-lg font-medium text-slate-600 dark:text-slate-400 mb-2">
                            No web URIs available
                        </h3>
                        <p className="text-slate-500 dark:text-slate-500">
                            No web URIs are currently assigned to your roles.
                        </p>
                    </CardContent>
                </Card>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-900 rounded-lg border">
                    <div className="text-sm text-slate-600 dark:text-slate-400">
                        Showing {((currentPage - 1) * URLS_PER_PAGE) + 1} to {Math.min(currentPage * URLS_PER_PAGE, webUrlsMapped.length)} of {webUrlsMapped.length} web URIs
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage <= 1}
                            className="gap-1"
                            data-testid="button-prev-page"
                        >
                            <ChevronLeft className="w-4 h-4" />
                            Previous
                        </Button>
                        <div className="flex items-center gap-1">
                            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                const page = i + 1;
                                return (
                                    <Button
                                        key={page}
                                        variant={currentPage === page ? "default" : "outline"}
                                        size="sm"
                                        onClick={() => handlePageChange(page)}
                                        className="w-8 h-8 p-0"
                                        data-testid={`button-page-${page}`}
                                    >
                                        {page}
                                    </Button>
                                );
                            })}
                            {totalPages > 5 && (
                                <>
                                    <span className="text-slate-500 px-2">...</span>
                                    <Button
                                        variant={currentPage === totalPages ? "default" : "outline"}
                                        size="sm"
                                        onClick={() => handlePageChange(totalPages)}
                                        className="w-8 h-8 p-0"
                                        data-testid={`button-page-${totalPages}`}
                                    >
                                        {totalPages}
                                    </Button>
                                </>
                            )}
                        </div>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage >= totalPages}
                            className="gap-1"
                            data-testid="button-next-page"
                        >
                            Next
                            <ChevronRight className="w-4 h-4" />
                        </Button>
                    </div>
                </div>
            )}
            {showAccessDialog && selectedUrl && <WebURLConnectAccess
                open={showAccessDialog}
                onOpenChange={() => setShowAccessDialog(false)}
                tenant={tenantName}
                webURL={selectedUrl}
            />}
        </div>

    );
}
